/**
 * Format bytes into human-readable size string
 *
 * @param bytes - Number of bytes to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with appropriate unit (B, KB, MB, GB, TB)
 *
 * @example
 * formatBytes(0) // "0 B"
 * formatBytes(1024) // "1.00 KB"
 * formatBytes(1536) // "1.50 KB"
 * formatBytes(1048576) // "1.00 MB"
 * formatBytes(1073741824) // "1.00 GB"
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  // Handle edge cases
  if (bytes === 0) return '0 B';
  if (bytes < 0) return `-${formatBytes(-bytes, decimals)}`;

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

  // Calculate the appropriate unit index
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Prevent index out of bounds (max out at TB)
  const index = Math.min(i, sizes.length - 1);

  // Calculate the value in the appropriate unit
  const value = bytes / Math.pow(k, index);

  // Format with specified decimal places
  return `${value.toFixed(dm)} ${sizes[index]}`;
}

/**
 * Format bytes into human-readable size string with compact formatting
 * Shows at least 2 significant digits
 *
 * @param bytes - Number of bytes to format
 * @returns Formatted string with appropriate unit
 *
 * @example
 * formatBytesCompact(0) // "0 B"
 * formatBytesCompact(1024) // "1.0 KB"
 * formatBytesCompact(1126) // "1.1 KB"
 * formatBytesCompact(10240) // "10 KB"
 * formatBytesCompact(1048576) // "1.0 MB"
 */
export function formatBytesCompact(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 0) return `-${formatBytesCompact(-bytes)}`;

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const index = Math.min(i, sizes.length - 1);
  const value = bytes / Math.pow(k, index);

  // Show at least 2 significant digits:
  // - For values < 10: show 1 decimal place (e.g., 1.0, 9.9)
  // - For values >= 10: no decimal places needed (e.g., 10, 99, 100)
  const decimals = value < 10 ? 1 : 0;

  return `${value.toFixed(decimals)} ${sizes[index]}`;
}
